import { useState, useMemo, useCallback } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { Link } from 'react-router-dom';
import {
  Plus,
  Target,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Edit3,
  TrendingUp,
  Wallet,
  PieChart,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { useCategoryBudgets, BudgetWithProgress } from '@/hooks/useCategoryBudgets';
import { useCategories } from '@/hooks/useCategories';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { CustomSelect } from '@/components/ui/custom-select';
import { cn } from '@/lib/utils';

export default function BudgetsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { t, language } = useTranslation();
  const confirm = useConfirm();
  const { formatAmount } = useCurrency();

  // Month selector
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return (
      selectedMonth.getFullYear() === now.getFullYear() &&
      selectedMonth.getMonth() === now.getMonth()
    );
  }, [selectedMonth]);

  const navigateMonth = useCallback((direction: -1 | 1) => {
    setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
  }, []);

  const goToCurrentMonth = useCallback(() => {
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const selectedMonthLabel = useMemo(() => {
    return selectedMonth.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
  }, [selectedMonth, language]);

  const { budgets, budgetStats, loading, saveBudget, deleteBudget } =
    useCategoryBudgets(selectedMonth);
  const { getCategoryIcon, getVisibleCategoriesGrouped } = useCategories();

  // Get categories without budget, grouped by category group
  const groupedCategoriesWithoutBudget = useMemo(() => {
    const budgetCategoryIds = new Set(budgets.map((b) => b.category_id));
    const grouped = getVisibleCategoriesGrouped('expense');
    // Filter out categories that already have budgets
    return grouped
      .map(({ group, categories }) => ({
        group,
        categories: categories.filter((c) => !budgetCategoryIds.has(c.id)),
      }))
      .filter(({ categories }) => categories.length > 0); // Only include groups with available categories
  }, [budgets, getVisibleCategoriesGrouped]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<BudgetWithProgress | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetPeriod, setBudgetPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [saving, setSaving] = useState(false);

  const handleOpenModal = (budget?: BudgetWithProgress) => {
    if (budget) {
      setEditingBudget(budget);
      setSelectedCategory(budget.category_id);
      setBudgetAmount(budget.amount_limit.toString());
      setBudgetPeriod(budget.period as 'weekly' | 'monthly' | 'yearly');
    } else {
      setEditingBudget(null);
      setSelectedCategory('');
      setBudgetAmount('');
      setBudgetPeriod('monthly');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBudget(null);
    setSelectedCategory('');
    setBudgetAmount('');
  };

  const handleSave = async () => {
    if (!selectedCategory || !budgetAmount) return;

    setSaving(true);
    const result = await saveBudget(selectedCategory, parseFloat(budgetAmount), budgetPeriod);
    setSaving(false);

    if (result.success) {
      handleCloseModal();
    }
  };

  const handleDelete = async (budgetId: string) => {
    const ok = await confirm({ description: t('budgets.delete.confirm'), variant: 'destructive' });
    if (!ok) return;
    await deleteBudget(budgetId);
  };

  const getProgressColor = (percentage: number, isOverBudget: boolean) => {
    if (isOverBudget) return 'bg-red-500';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-[#16c47f]';
  };

  const getStatusBadge = (budget: BudgetWithProgress) => {
    if (budget.isOverBudget) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {t('budgets.status.exceeded')}
        </span>
      );
    }
    if (budget.percentage >= 80) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {t('budgets.status.nearLimit')}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" />
        {t('budgets.status.healthy')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="budgets" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        <PageHeader
          title={t('budgets.title')}
          subtitle={t('budgets.subtitle')}
          action={
            <div className="flex items-center gap-3">
              <Link
                to="/settings?tab=categories"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-[#16c47f] dark:hover:text-[#16c47f] hover:bg-[#16c47f]/10 rounded-xl transition-all border border-gray-100 dark:border-gray-700/50"
              >
                <Settings className="w-4 h-4" />
                {t('transactions.customizeCategories')}
              </Link>
              <button
                id="guide-budgets"
                onClick={() => handleOpenModal()}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md scroll-mt-24 target:ring-4 target:ring-[#16c47f]/30 target:ring-offset-4"
              >
                <Plus className="w-4 h-4" />
                {t('budgets.addNew')}
              </button>
            </div>
          }
        />

        <div className="p-6 space-y-6">
          {/* Month Navigation */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1.5 bg-gray-100/80 dark:bg-gray-700/60 rounded-xl p-1.5">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white hover:shadow-sm transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span
                  className={`px-4 py-2 text-sm font-semibold rounded-lg min-w-[140px] text-center capitalize ${
                    isCurrentMonth
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {selectedMonthLabel}
                </span>
                <button
                  onClick={() => navigateMonth(1)}
                  disabled={isCurrentMonth}
                  className={`p-2 rounded-lg transition-all ${
                    isCurrentMonth
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {!isCurrentMonth && (
                <button
                  onClick={goToCurrentMonth}
                  className="h-10 px-3.5 flex items-center gap-2 rounded-xl bg-[#16c47f]/10 hover:bg-[#16c47f]/20 border border-[#16c47f]/20 hover:border-[#16c47f]/40 text-[#16c47f] text-xs font-semibold transition-all"
                >
                  {language === 'fr' ? 'Ce mois' : 'This month'}
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#16c47f] to-emerald-600 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('budgets.stats.totalBudget')}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatAmount(budgetStats.totalBudget)}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('budgets.stats.totalSpent')}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatAmount(budgetStats.totalSpent)}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('budgets.stats.remaining')}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatAmount(budgetStats.totalRemaining)}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                  <PieChart className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('budgets.stats.usage')}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {budgetStats.overallPercentage.toFixed(0)}%
              </p>
            </div>
          </div>

          {/* Status Overview */}
          {budgets.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {budgetStats.healthyCount > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    {budgetStats.healthyCount} {t('budgets.healthyBudgets')}
                  </span>
                </div>
              )}
              {budgetStats.nearLimitCount > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    {budgetStats.nearLimitCount} {t('budgets.nearLimitBudgets')}
                  </span>
                </div>
              )}
              {budgetStats.overBudgetCount > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    {budgetStats.overBudgetCount} {t('budgets.exceededBudgets')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Budget List */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-10 h-10 border-4 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : budgets.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-1">{t('budgets.empty')}</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mb-4">
                  {t('budgets.emptyDesc')}
                </p>
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium rounded-xl transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('budgets.addFirst')}
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {budgets.map((budget) => (
                  <div
                    key={budget.id}
                    className="p-5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ backgroundColor: `${budget.category?.color}20` }}
                        >
                          {getCategoryIcon(budget.category?.icon || '')}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {budget.category?.name || 'Unknown Category'}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t(`budgets.period.${budget.period}`)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(budget)}
                        <button
                          onClick={() => handleOpenModal(budget)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(budget.id)}
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all duration-500',
                            getProgressColor(budget.percentage, budget.isOverBudget)
                          )}
                          style={{ width: `${Math.min(100, budget.percentage)}%` }}
                        />
                      </div>
                    </div>

                    {/* Amount Info */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('budgets.spent')}:{' '}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatAmount(budget.spent)}
                        </span>
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('budgets.limit')}:{' '}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatAmount(budget.amount_limit)}
                        </span>
                      </span>
                      <span
                        className={cn(
                          'font-semibold',
                          budget.isOverBudget ? 'text-red-500' : 'text-[#16c47f]'
                        )}
                      >
                        {budget.isOverBudget ? '-' : ''}
                        {formatAmount(Math.abs(budget.remaining))} {t('budgets.remaining')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-xl">
              <div className="p-6 border-b border-gray-100 dark:border-gray-700/50">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingBudget ? t('budgets.modal.edit') : t('budgets.modal.create')}
                </h2>
              </div>

              <div className="p-6 space-y-4">
                {/* Category Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('budgets.modal.category')}
                  </label>
                  <CustomSelect
                    value={selectedCategory}
                    onChange={(value) => setSelectedCategory(value)}
                    disabled={!!editingBudget}
                    placeholder={t('budgets.modal.selectCategory')}
                    options={
                      editingBudget
                        ? [
                            {
                              value: editingBudget.category_id,
                              label: `${getCategoryIcon(editingBudget.category?.icon || '')} ${editingBudget.category?.name}`,
                            },
                          ]
                        : groupedCategoriesWithoutBudget.flatMap(({ group, categories }) =>
                            categories.map((category) => ({
                              value: category.id,
                              label: `${getCategoryIcon(category.icon || '')} ${category.name}`,
                              group: group.name,
                            }))
                          )
                    }
                  />
                </div>

                {/* Amount Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('budgets.modal.amount')}
                  </label>
                  <input
                    type="number"
                    value={budgetAmount}
                    onChange={(e) => setBudgetAmount(e.target.value)}
                    placeholder="500"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                  />
                </div>

                {/* Period Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('budgets.modal.period')}
                  </label>
                  <CustomSelect
                    value={budgetPeriod}
                    onChange={(value) => setBudgetPeriod(value as 'weekly' | 'monthly' | 'yearly')}
                    options={[
                      { value: 'weekly', label: t('budgets.period.weekly') },
                      { value: 'monthly', label: t('budgets.period.monthly') },
                      { value: 'yearly', label: t('budgets.period.yearly') },
                    ]}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-gray-700/50 flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!selectedCategory || !budgetAmount || saving}
                  className="flex-1 px-4 py-3 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
